import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { API_URL as API_BASE } from "../config";

const PRICING_API = `${API_BASE}/pricing`;
const DEFAULT_CUSTOMER_DISCLAIMER =
    "Preliminary, non-binding estimate only. This document is for planning purposes and is not a formal quotation, offer, proposal, contract, or commitment to perform work. Pricing, scope, schedule, and customer-provided equipment assumptions are fully variable and subject to adjustment. Travel expenses are not included. A binding agreement requires a formal written quotation or proposal accepted in writing by both parties and, where applicable, a mutually executed contract.";
const DEFAULT_BRAND_SLOGAN = "Automation Simplified. Point Blank.";

const ROUND_OPTIONS = [
    { value: "none", label: "No rounding (show cents)" },
    { value: "nearest_10", label: "Nearest $10" },
    { value: "nearest_50", label: "Nearest $50" },
    { value: "nearest_100", label: "Nearest $100 — quick verbal" },
    { value: "nearest_250", label: "Nearest $250" },
    { value: "nearest_500", label: "Nearest $500" },
    { value: "nearest_1000", label: "Nearest $1k" },
];

function newKey(prefix) {
    try {
        const c = typeof window !== "undefined" ? window.crypto : undefined;
        if (c?.randomUUID) return `${prefix}_${c.randomUUID().slice(0, 8)}`;
    } catch {
        /* ignore */
    }
    return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const Pricing = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    const [currency, setCurrency] = useState("USD");
    const [materialMarkup, setMaterialMarkup] = useState(12);
    const [contingency, setContingency] = useState(8);
    const [tax, setTax] = useState(0);
    const [rounding, setRounding] = useState("nearest_100");
    const [customerDisclaimer, setCustomerDisclaimer] = useState(DEFAULT_CUSTOMER_DISCLAIMER);
    const [brandSlogan, setBrandSlogan] = useState(DEFAULT_BRAND_SLOGAN);
    const [brandSloganSource, setBrandSloganSource] = useState("local");

    const [rates, setRates] = useState([]);
    const [presets, setPresets] = useState([]);

    const rateKeys = useMemo(() => new Set((rates || []).map((r) => String(r.rate_key))), [rates]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(PRICING_API);
            const m = res.data || {};
            const s = m.settings || {};
            setCurrency(s.currency || "USD");
            setMaterialMarkup(Number(s.material_markup_percent) || 12);
            setContingency(Number(s.contingency_percent) || 8);
            setTax(Number(s.tax_rate_percent) || 0);
            setRounding(s.customer_total_rounding || "nearest_100");
            setCustomerDisclaimer(s.customer_disclaimer || DEFAULT_CUSTOMER_DISCLAIMER);
            setBrandSlogan(s.brand_slogan || DEFAULT_BRAND_SLOGAN);
            setBrandSloganSource(s.brand_slogan_source || "local");

            const normRates = Array.isArray(m.rates)
                ? m.rates.map((r, idx) => ({
                    rate_key: String(r.rate_key || ""),
                    label: String(r.label || ""),
                    unit: String(r.unit || "hour").toLowerCase(),
                    description: r.description != null ? String(r.description) : "",
                    suggested_rate: Number(r.suggested_rate) || 0,
                    sort_order: Number(r.sort_order) || idx,
                }))
                : [];
            normRates.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            setRates(normRates);

            const normPresets = Array.isArray(m.presets)
                ? m.presets.map((p, idx) => ({
                    preset_key: String(p.preset_key || ""),
                    label: String(p.label || ""),
                    rate_key: String(p.rate_key || ""),
                    unit: String(p.unit || "hour").toLowerCase(),
                    default_qty: Number(p.default_qty) || 1,
                    sort_order: Number(p.sort_order) || idx,
                }))
                : [];
            normPresets.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            setPresets(normPresets);
        } catch (e) {
            setToast({ open: true, message: "Failed to load pricing model.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const normalizeRateRows = () => {
        const out = rates.map((r, idx) => {
            const rate_key = (r.rate_key || "").trim() || `rate_${idx + 1}`;
            const unit = ["hour", "day", "flat"].includes(String(r.unit)) ? String(r.unit) : "hour";
            return {
                rate_key,
                label: (r.label || "").trim() || rate_key,
                unit,
                description: r.description != null ? String(r.description).trim() : null,
                suggested_rate: Number(r.suggested_rate) || 0,
                sort_order: idx,
            };
        });

        const keys = out.map((r) => r.rate_key);
        if (keys.length !== new Set(keys).size) throw new Error("Duplicate rate keys — each bucket needs a unique key.");
        if (out.length < 1) throw new Error("Keep at least one labor bucket.");
        return out;
    };

    const normalizePresetRows = (finalRatesKeys) => {
        return presets.map((p, idx) => {
            const preset_key = (p.preset_key || "").trim() || `preset_${idx + 1}`;
            const rate_key = (p.rate_key || "").trim();
            const unit = ["hour", "day", "flat"].includes(String(p.unit)) ? String(p.unit) : "hour";

            const rq = Number(p.default_qty);
            const default_qty = Number.isFinite(rq) && rq > 0 ? rq : 1;
            const label = (p.label || "").trim() || preset_key;

            if (!finalRatesKeys.has(rate_key)) {
                throw new Error(`Preset “${label}” points at missing bucket “${rate_key}”.`);
            }
            return { preset_key, label, rate_key, unit, default_qty, sort_order: idx };
        });
    };

    const handleSave = async () => {
        let normRates = [];
        let normPresets = [];
        try {
            normRates = normalizeRateRows();
            normPresets = normalizePresetRows(new Set(normRates.map((r) => r.rate_key)));
        } catch (e) {
            setToast({ open: true, message: e.message || "Validation failed.", severity: "warning" });
            return;
        }

        setSaving(true);
        try {
            await axios.put(PRICING_API, {
                settings: {
                    currency,
                    material_markup_percent: Number(materialMarkup) || 0,
                    contingency_percent: Number(contingency) || 0,
                    tax_rate_percent: Number(tax) || 0,
                    customer_total_rounding: rounding,
                    customer_disclaimer: customerDisclaimer.trim() || DEFAULT_CUSTOMER_DISCLAIMER,
                    brand_slogan: brandSlogan.trim() || DEFAULT_BRAND_SLOGAN,
                },
                rates: normRates,
                presets: normPresets,
            });
            setToast({ open: true, message: "Pricing model saved.", severity: "success" });
            await load();
        } catch (e) {
            const msg = e?.response?.data?.error || "Save failed.";
            setToast({ open: true, message: msg, severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    const addRate = () =>
        setRates((rows) => [
            ...rows,
            {
                rate_key: newKey("bucket"),
                label: "New labor bucket",
                unit: "hour",
                description: "",
                suggested_rate: 115,
                sort_order: rows.length,
            },
        ]);

    const addPreset = () =>
        setPresets((rows) => {
            const firstKey = rates[0]?.rate_key || "";
            return [
                ...rows,
                {
                    preset_key: newKey("preset"),
                    label: "New template",
                    rate_key: firstKey,
                    unit: "hour",
                    default_qty: 8,
                    sort_order: rows.length,
                },
            ];
        });

    return (
        <Stack spacing={2} sx={{ pb: 4 }}>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 850, letterSpacing: "0.02em" }}>
                    Quote Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 940 }}>
                    These assumptions power the quote worksheet calculators. Separate catalog buckets capture how you truly sell labor
                    (hourly vs onsite day bundles vs flat scopes), while markups bake in procurement lift. Quotes snapshot the billed
                    rate per line — tune the catalog knowing history stays intact below.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Material markup stacks on purchased cost you enter per quote (not ERP perfect, but decisive on a deadline). Bump
                    contingency when customers are exploratory or scope is handwritten on a cocktail napkin.
                </Typography>
            </Box>

            {loading ? <Alert severity="info">Loading your pricing playbook…</Alert> : null}
            {!loading && rates.length === 0 ? <Alert severity="warning">No buckets found — reload after backend seeds or tap Add bucket.</Alert> : null}

            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 850, mb: 2 }}>
                    Global assumptions
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Currency ISO code"
                            fullWidth
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 8))}
                            helperText="Used for Intl formatting hints"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Material markup %"
                            type="number"
                            fullWidth
                            value={materialMarkup}
                            onChange={(e) => setMaterialMarkup(Number(e.target.value))}
                            helperText="% over your cash cost"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Contingency / risk buffer %"
                            type="number"
                            fullWidth
                            value={contingency}
                            onChange={(e) => setContingency(Number(e.target.value))}
                            helperText="Applied after labor + material sell"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Tax % (informational)"
                            type="number"
                            fullWidth
                            value={tax}
                            onChange={(e) => setTax(Number(e.target.value))}
                            helperText="Stay 0 unless you truly collect it"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="rounding-label">Customer ballpark rounding</InputLabel>
                            <Select
                                labelId="rounding-label"
                                label="Customer ballpark rounding"
                                value={rounding}
                                onChange={(e) => setRounding(e.target.value)}
                            >
                                {ROUND_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                                <MenuItem value="nearest_1">Nearest whole dollar ($1)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Brand slogan"
                            fullWidth
                            value={brandSlogan}
                            InputProps={{ readOnly: true }}
                            helperText={
                                brandSloganSource === "cms"
                                    ? "From website CMS (siteCopy → brandSlogan). Edit in Admin → Tools → Site copy. Used on the public footer and customer quote PDFs."
                                    : "CMS unavailable — showing local cache. Edit on the website (Admin → Tools → Site copy), then reload this page."
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Quote language / customer disclaimer"
                            fullWidth
                            multiline
                            minRows={4}
                            value={customerDisclaimer}
                            onChange={(e) => setCustomerDisclaimer(e.target.value)}
                            helperText="Printed on customer and internal PDFs. Keep this clear about non-binding estimates, exclusions, and formal agreement requirements."
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                        Labor & field rate catalog
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={addRate}>
                        Add bucket
                    </Button>
                </Stack>
                <Stack spacing={1.5}>
                    {rates.map((r, idx) => (
                        <Paper key={`${r.rate_key}__${idx}`} variant="outlined" sx={{ p: 1.5 }}>
                            <Grid container spacing={1.5} alignItems="flex-start">
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Customer-facing label"
                                        value={r.label}
                                        onChange={(e) =>
                                            setRates((rows) =>
                                                rows.map((row, i) => (i === idx ? { ...row, label: e.target.value } : row))
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Key"
                                        value={r.rate_key}
                                        onChange={(e) =>
                                            setRates((rows) =>
                                                rows.map((row, i) => (i === idx ? { ...row, rate_key: e.target.value } : row))
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3} md={1.5}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Unit</InputLabel>
                                        <Select
                                            label="Unit"
                                            value={r.unit || "hour"}
                                            onChange={(e) =>
                                                setRates((rows) =>
                                                    rows.map((row, i) => (i === idx ? { ...row, unit: e.target.value } : row))
                                                )
                                            }
                                        >
                                            <MenuItem value="hour">hour</MenuItem>
                                            <MenuItem value="day">day</MenuItem>
                                            <MenuItem value="flat">flat</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3} md={1.5}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Default rate"
                                        value={String(r.suggested_rate ?? "")}
                                        onChange={(e) =>
                                            setRates((rows) =>
                                                rows.map((row, i) =>
                                                    i === idx ? { ...row, suggested_rate: Number(e.target.value) || 0 } : row
                                                )
                                            )
                                        }
                                        inputProps={{ inputMode: "decimal" }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={11}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Coaching tip"
                                        value={r.description}
                                        onChange={(e) =>
                                            setRates((rows) =>
                                                rows.map((row, i) => (i === idx ? { ...row, description: e.target.value } : row))
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={1} sx={{ display: "flex", justifyContent: { xs: "flex-end", md: "center" }, pt: { md: 0.5 } }}>
                                    <IconButton
                                        aria-label="Delete bucket"
                                        size="small"
                                        onClick={() => setRates((rows) => rows.filter((_row, i) => i !== idx))}
                                        disabled={rates.length <= 1}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                </Stack>
            </Paper>

            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                            Estimate presets
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Quick-add chips on a quote. Each preset is a label, a rate bucket, and a starting quantity.
                        </Typography>
                    </Box>
                    <Button size="small" startIcon={<AddIcon />} onClick={addPreset} disabled={rates.length === 0}>
                        Add preset
                    </Button>
                </Stack>
                <Stack spacing={1.5}>
                    {presets.map((p, idx) => (
                        <Paper key={`${p.preset_key}__${idx}`} variant="outlined" sx={{ p: 1.5 }}>
                            <Grid container spacing={1.5} alignItems="flex-start">
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Label"
                                        value={p.label}
                                        onChange={(e) =>
                                            setPresets((rows) =>
                                                rows.map((row, i) => (i === idx ? { ...row, label: e.target.value } : row))
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={7}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Bucket</InputLabel>
                                        <Select
                                            label="Bucket"
                                            value={rateKeys.has(p.rate_key) ? p.rate_key : ""}
                                            displayEmpty
                                            onChange={(e) =>
                                                setPresets((rows) =>
                                                    rows.map((row, i) => (i === idx ? { ...row, rate_key: e.target.value } : row))
                                                )
                                            }
                                        >
                                            <MenuItem value="">
                                                <em>Pick bucket</em>
                                            </MenuItem>
                                            {rates.map((r) => (
                                                <MenuItem key={r.rate_key} value={r.rate_key}>
                                                    {r.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={5}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Key"
                                        value={p.preset_key}
                                        onChange={(e) =>
                                            setPresets((rows) =>
                                                rows.map((row, i) => (i === idx ? { ...row, preset_key: e.target.value } : row))
                                            )
                                        }
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3} md={2.5}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Unit</InputLabel>
                                        <Select
                                            label="Unit"
                                            value={p.unit || "hour"}
                                            onChange={(e) =>
                                                setPresets((rows) =>
                                                    rows.map((row, i) => (i === idx ? { ...row, unit: e.target.value } : row))
                                                )
                                            }
                                        >
                                            <MenuItem value="hour">hour</MenuItem>
                                            <MenuItem value="day">day</MenuItem>
                                            <MenuItem value="flat">flat</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={3} md={2.5}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Default qty"
                                        value={String(p.default_qty ?? "")}
                                        onChange={(e) =>
                                            setPresets((rows) =>
                                                rows.map((row, i) =>
                                                    i === idx ? { ...row, default_qty: Number(e.target.value) || 0 } : row
                                                )
                                            )
                                        }
                                        inputProps={{ inputMode: "decimal" }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: { xs: "flex-end", md: "center" }, pt: { md: 0.5 } }}>
                                    <IconButton
                                        aria-label="Delete preset"
                                        size="small"
                                        onClick={() => setPresets((rows) => rows.filter((_row, i) => i !== idx))}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                    {rates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Add buckets first — presets latch onto bucket pricing.
                        </Typography>
                    ) : null}
                </Stack>

                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
                    <Button variant="outlined" disabled={loading} onClick={() => load()}>
                        Reload
                    </Button>
                    <Button variant="contained" disabled={loading || saving} startIcon={<SaveIcon />} onClick={handleSave}>
                        Save playbook
                    </Button>
                </Stack>
            </Paper>

            <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
                <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: "100%" }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

export default Pricing;
